<app-topbar></app-topbar>
<app-sidebar></app-sidebar>

<div class="app-content content">
  <div class="content-wrapper">
    <div class="content-header">
      <div class="page-head">
        <div>
          <h3 class="page-head__title">Reconciliation</h3>
          <p class="page-head__sub">
            Compare each IMTO partner&rsquo;s wallet ledger against the payment
            processor (Rave) cash balance.
          </p>
        </div>
        <div class="page-head__tools">
          @if (lastUpdated) {
            <span class="recon-updated">
              <i class="fa fa-clock-o" aria-hidden="true"></i>
              Updated {{ lastUpdated | date: 'HH:mm:ss' }}
            </span>
          }
          <button
            class="btn btn-outline-success btn-round"
            type="button"
            (click)="load()"
            [disabled]="isLoading || !canAdjust"
            >
            <i
              class="fa"
              [ngClass]="isLoading ? 'fa-spinner fa-spin' : 'fa-refresh'"
              aria-hidden="true"
            ></i>
            {{ isLoading ? 'Refreshing…' : 'Refresh Balances' }}
          </button>
        </div>
      </div>
    </div>

    <div class="content-body">
      @if (loadError) {
        <div class="alert alert-danger">{{ loadError }}</div>
      }

      @if (canAdjust) {
        @if (rows.length > 0) {
          <div class="recon-kpis">
            <div class="recon-kpi">
              <span class="recon-kpi__label">
                <i class="fa fa-google-wallet" aria-hidden="true"></i>
                Total IMTO Wallet
              </span>
              <span class="recon-kpi__value">{{ money(platformRow?.wallet_balance) }}</span>
            </div>
            <div class="recon-kpi">
              <span class="recon-kpi__label">
                <i class="fa fa-university" aria-hidden="true"></i>
                Processor (Rave) Cash
              </span>
              <span class="recon-kpi__value">{{ money(platformRow?.processor_balance) }}</span>
              @if (platformRow?.processor_currency) {
                <span class="recon-kpi__meta">{{ platformRow.processor_currency }}</span>
              }
            </div>
            <div class="recon-kpi" [class.recon-kpi--warn]="totalUnexplained !== 0">
              <span class="recon-kpi__label">
                <i class="fa fa-search" aria-hidden="true"></i>
                Unexplained
              </span>
              <span class="recon-kpi__value" [ngClass]="changeClass(totalUnexplained)">
                {{ signedMoney(totalUnexplained) }}
              </span>
            </div>
            <div class="recon-kpi">
              <span class="recon-kpi__label">
                <i class="fa fa-sliders" aria-hidden="true"></i>
                Net Adjustments
              </span>
              <span class="recon-kpi__value" [ngClass]="changeClass(totalAdjustments)">
                {{ signedMoney(totalAdjustments) }}
              </span>
            </div>
            <div class="recon-kpi">
              <span class="recon-kpi__label">
                <i class="fa fa-users" aria-hidden="true"></i>
                Partners
              </span>
              <span class="recon-kpi__value recon-kpi__value--pills">
                <span class="recon-pill recon-pill--ok">{{ closeCount }} close</span>
                <span
                  class="recon-pill"
                  [ngClass]="reviewCount ? 'recon-pill--bad' : 'recon-pill--muted'"
                  >{{ reviewCount }} review</span
                  >
                </span>
              </div>
            </div>
          }

          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header card-toolbar">
                  <h4 class="card-toolbar__title">
                    <i class="fa fa-balance-scale" aria-hidden="true"></i>
                    Wallet vs Processor Balance
                  </h4>
                  <button
                    class="recon-guide-toggle"
                    type="button"
                    (click)="showGuide = !showGuide"
                    [attr.aria-expanded]="showGuide"
                    >
                    <i
                      class="fa"
                      [ngClass]="showGuide ? 'fa-chevron-up' : 'fa-info-circle'"
                      aria-hidden="true"
                    ></i>
                    How is this calculated?
                  </button>
                </div>
                <div class="card-content">
                  @if (showGuide) {
                    <p class="recon-note">
                      The IMTO wallet is a <b>ledger</b> while the processor
                      balance is <b>cash</b>, so they are not expected to be
                      identical. They are <b>&ldquo;close&rdquo;</b> when the gap
                      between them is accounted for by in-flight transfers, failed
                      transfers not yet reversed, and recorded balance
                      corrections. Fundings booked into the wallet (with cash
                      moved separately) and processor fees are the usual reasons
                      for a persistent gap. Use <b>Adjust</b> on a partner row to
                      correct the wallet to the processor-backed position when a
                      discrepancy is confirmed.
                    </p>
                  }

                  @if (isLoading && rows.length === 0) {
                    <div class="recon-loading">
                      <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                      Loading balances…
                    </div>
                  }

                  @if (!isLoading && rows.length === 0) {
                    <div class="empty-state">No IMTO partners found.</div>
                  }

                  @if (rows.length > 0) {
                    <div class="table-responsive recon-table-wrap">
                      <table class="table table-de mb-0 recon-table">
                        <thead>
                          <tr>
                            <th>IMTO partner</th>
                            <th class="num">Wallet Balance</th>
                            <th class="num">Processor Balance</th>
                            <th class="num">Gap</th>
                            <th class="num">Success Payouts</th>
                            <th class="num">In Flight</th>
                            <th class="num">Failed Unresolved</th>
                            <th class="num">Fundings (Wallet)</th>
                            <th class="num">Adjustments</th>
                            <th class="num">Unexplained</th>
                            <th>Status</th>
                            <th class="actions-col"></th>
                          </tr>
                        </thead>
                        <tbody>
                          @for (row of rows; track row?.agent?.slug) {
                            <tr [class.recon-platform]="row?.agent?.slug === 'platform'">
                              <td class="recon-partner">{{ row?.agent?.name }}</td>
                              <td class="num">{{ money(row?.wallet_balance) }}</td>
                              <td class="num">
                                @if (row?.processor_balance !== null && row?.processor_balance !== undefined) {
                                  {{ money(row?.processor_balance) }}
                                  @if (row?.processor_currency) {
                                    <span class="recon-ccy">({{ row?.processor_currency }})</span>
                                  }
                                } @else {
                                  <span class="recon-mute">—</span>
                                }
                              </td>
                              <td class="num">
                                @if (row?.gap !== null && row?.gap !== undefined) {
                                  <span [ngClass]="changeClass(row?.gap)">{{ signedMoney(row?.gap) }}</span>
                                } @else {
                                  <span class="recon-mute">—</span>
                                }
                              </td>
                              <td class="num">
                                @if (row?.success_payouts?.count) {
                                  <span class="recon-count">{{ row?.success_payouts?.count }}</span>
                                  <span class="recon-amt">{{ money(row?.success_payouts?.sum) }}</span>
                                } @else {
                                  <span class="recon-mute">0</span>
                                }
                              </td>
                              <td class="num">
                                @if (row?.in_flight?.count) {
                                  <span class="recon-count">{{ row?.in_flight?.count }}</span>
                                  <span class="recon-amt">{{ money(row?.in_flight?.sum) }}</span>
                                } @else {
                                  <span class="recon-mute">0</span>
                                }
                              </td>
                              <td class="num">
                                @if (row?.failed_unresolved?.count) {
                                  <span class="recon-count recon-neg">{{ row?.failed_unresolved?.count }}</span>
                                  <span class="recon-amt">{{ money(row?.failed_unresolved?.sum) }}</span>
                                } @else {
                                  <span class="recon-mute">0</span>
                                }
                              </td>
                              <td class="num">
                                @if (row?.active_fundings?.count) {
                                  <span class="recon-count">{{ row?.active_fundings?.count }}</span>
                                  <span class="recon-amt">{{ money(row?.active_fundings?.sum) }}</span>
                                } @else {
                                  <span class="recon-mute">0</span>
                                }
                              </td>
                              <td class="num">
                                @if (row?.adjustments?.count) {
                                  <span class="recon-count">{{ row?.adjustments?.count }}</span>
                                  <span class="recon-amt" [ngClass]="changeClass(row?.adjustments?.sum)">
                                    {{ signedMoney(row?.adjustments?.sum) }}
                                  </span>
                                } @else {
                                  <span class="recon-mute">0</span>
                                }
                              </td>
                              <td class="num">
                                @if (row?.unexplained !== null && row?.unexplained !== undefined) {
                                  <span [ngClass]="changeClass(row?.unexplained)">
                                    {{ signedMoney(row?.unexplained) }}
                                  </span>
                                } @else {
                                  <span class="recon-mute">—</span>
                                }
                              </td>
                              <td>
                                @if (row?.is_close !== null && row?.is_close !== undefined) {
                                  <span
                                    [ngClass]="{
                                      'status-badge status-success': row?.is_close,
                                      'status-badge status-failed': !row?.is_close
                                    }"
                                    >
                                    {{ row?.is_close ? 'CLOSE' : 'REVIEW' }}
                                  </span>
                                } @else {
                                  <span class="recon-mute">—</span>
                                }
                              </td>
                              @if (canAdjust && row?.agent?.id) {
                                <td class="actions-col">
                                  <button
                                    type="button"
                                    class="recon-btn recon-btn--ghost"
                                    (click)="openAdjustDetails(row)"
                                    [disabled]="!historyFor(row).length"
                                    [title]="
                                      historyFor(row).length
                                        ? 'View adjustment history'
                                        : 'No adjustments recorded'
                                    "
                                    >
                                    <i class="fa fa-history" aria-hidden="true"></i>
                                    History
                                    @if (historyFor(row).length) {
                                      <span class="recon-badge">{{ historyFor(row).length }}</span>
                                    }
                                  </button>
                                  <button
                                    type="button"
                                    class="recon-btn recon-btn--primary"
                                    (click)="openAdjust(row)"
                                    title="Adjust IMTO wallet balance"
                                    >
                                    <i class="fa fa-sliders" aria-hidden="true"></i>
                                    Adjust
                                  </button>
                                </td>
                              }
                            </tr>
                          }
                        </tbody>
                      </table>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  </div>

  <app-bottombar></app-bottombar>

  @if (showAdjustModal) {
    <div class="modal-backdrop">
      <div class="modal-box">
        <div class="modal-head">
          <h3>Adjust IMTO Balance</h3>
          <button class="modal-close" type="button" (click)="closeAdjust()" aria-label="Close">
            &times;
          </button>
        </div>
        <p class="modal-subtitle">{{ adjustTarget?.agent?.name }}</p>

        <div class="recon-preview">
          <div class="recon-preview__cell">
            <span class="recon-preview__label">Current</span>
            <span class="recon-preview__value">{{ money(adjustTarget?.wallet_balance) }}</span>
          </div>
          <i class="fa fa-long-arrow-right recon-preview__arrow" aria-hidden="true"></i>
          <div class="recon-preview__cell">
            <span class="recon-preview__label">Corrected</span>
            <span class="recon-preview__value">{{ money(adjustForm.corrected_balance) }}</span>
          </div>
          <div class="recon-preview__cell">
            <span class="recon-preview__label">Change</span>
            <span class="recon-preview__value" [ngClass]="changeClass(adjustPreviewChange)">
              {{ signedMoney(adjustPreviewChange) }}
            </span>
          </div>
        </div>

        <label for="corrected-balance">Corrected Balance (NGN)</label>
        <input
          id="corrected-balance"
          type="number"
          step="0.01"
          [(ngModel)]="adjustForm.corrected_balance"
          />
        <label for="adjust-reason">Reason <span class="recon-req">*</span></label>
        <input
          id="adjust-reason"
          type="text"
          maxlength="255"
          [(ngModel)]="adjustForm.reason"
          placeholder="e.g. Matched to processor balance after funding deposit"
          />
        <div class="recon-hint">
          <i class="fa fa-lock" aria-hidden="true"></i>
          Adjustments are permanent and recorded in the audit trail against your
          admin account.
        </div>

        @if (adjustError) {
          <div class="alert alert-danger" style="margin-top: 10px">
            {{ adjustError }}
          </div>
        }
        <div class="actions">
          <button class="btn cancel" type="button" (click)="closeAdjust()" [disabled]="isSaving">
            Cancel
          </button>
          <button
            class="btn save"
            type="button"
            [disabled]="isSaving || !adjustValid"
            (click)="submitAdjust()"
            >
            @if (isSaving) {
              <span><i class="fa fa-spinner fa-spin" aria-hidden="true"></i> Saving…</span>
            } @else {
              <span>Save Adjustment</span>
            }
          </button>
        </div>
      </div>
    </div>
  }

  @if (showAdjustDetails) {
    <div class="modal-backdrop">
      <div class="modal-box modal-box--wide">
        <div class="modal-head">
          <h3>Adjustment History</h3>
          <button
            class="modal-close"
            type="button"
            (click)="closeAdjustDetails()"
            aria-label="Close"
            >
            &times;
          </button>
        </div>
        <p class="modal-subtitle">{{ adjustDetailsTarget?.agent?.name }}</p>

        @if (historyFor(adjustDetailsTarget).length) {
          <div class="recon-summary">
            <div class="recon-summary__cell">
              <span class="recon-summary__label">Records</span>
              <span class="recon-summary__value">{{ historyFor(adjustDetailsTarget).length }}</span>
            </div>
            <div class="recon-summary__cell">
              <span class="recon-summary__label">Net change</span>
              <span
                class="recon-summary__value"
                [ngClass]="changeClass(historyNet(adjustDetailsTarget))"
                >
                {{ signedMoney(historyNet(adjustDetailsTarget)) }}
              </span>
            </div>
            <div class="recon-summary__cell">
              <span class="recon-summary__label">Latest</span>
              <span class="recon-summary__value">
                {{ historyFor(adjustDetailsTarget)[0]?.created_at | date: 'yyyy-MM-dd HH:mm' }}
              </span>
            </div>
          </div>

          <div class="table-responsive recon-details-wrap">
            <table class="table table-de mb-0 recon-details">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Adjusted By</th>
                  <th class="num">Previous</th>
                  <th class="num">Corrected</th>
                  <th class="num">Change</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                @for (adj of historyFor(adjustDetailsTarget); track adj?.id) {
                  <tr>
                    <td>{{ adj?.created_at | date: 'yyyy-MM-dd HH:mm' }}</td>
                    <td>{{ adj?.adjusted_by || '—' }}</td>
                    <td class="num">{{ money(adj?.previous_balance) }}</td>
                    <td class="num">{{ money(adj?.corrected_balance) }}</td>
                    <td class="num">
                      <span [ngClass]="changeClass(adj?.change_amount)">
                        {{ signedMoney(adj?.change_amount) }}
                      </span>
                    </td>
                    <td class="recon-reason">{{ adj?.reason || '—' }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        } @else {
          <div class="empty-state">No adjustments have been recorded for this partner.</div>
        }

        <div class="actions">
          <button class="btn cancel" type="button" (click)="closeAdjustDetails()">Close</button>
        </div>
      </div>
    </div>
  }
